import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../routes/routes';
import { Navbar as BsNavbar, Nav, NavDropdown, Container, Button } from 'react-bootstrap';
import { CommentsHub } from '@/pages/fulfillment/components/CommentsHub';
import { useAuth } from '../../providers/AuthProvider';
import { ConfigReader } from '../../config/ConfigReader';
import { useSecretsAccess } from '@/apps/apis/secrets/hooks';
import { useFulfillmentV2Launch } from '@/infrastructure/FulfillmentV2Launch';
import {
  GitMerge,
  CheckCircle,
  Gift,
  Edit,
  List,
  Package,
  Users,
  Award,
  Bot,
  Bell,
  Building2,
  MessageSquare,
  BookOpen,
  FileText,
  Activity,
  FileQuestion,
  ScanFace,
  Tags,
  FlaskConical,
  LayoutDashboard,
  KeyRound,
} from 'lucide-react';

export const Navbar = () => {
  const [expanded, setExpanded] = useState(false);
  const { user, logout } = useAuth();
  const config = ConfigReader.getInstance().getConfig();
  // Hidden unless the Secrets API answers, so this admin can ship before the API does.
  const secretsAccess = useSecretsAccess();

  const handleToggle = () => setExpanded(!expanded);
  const handleClose = () => setExpanded(false);
  // With Fulfillment V2 launched the OMS order and stock screens are gone (SSD-7090), and the seven
  // screens that never were V1 order screens move under Fulfillment.
  const launch = useFulfillmentV2Launch();

  const picManagementItem = (
    <NavDropdown.Item as={Link} to={ROUTES.oms.picManagement} onClick={handleClose}>
      PIC Management
    </NavDropdown.Item>
  );
  const gigWorkersItem = (
    <NavDropdown.Item as={Link} to={ROUTES.oms.gigWorkers} onClick={handleClose}>
      Gig Workers
    </NavDropdown.Item>
  );
  const shippingFeeItem = (
    <NavDropdown.Item as={Link} to={ROUTES.oms.shippingFee} onClick={handleClose}>
      Shipping Fee
    </NavDropdown.Item>
  );
  const customsNamePromptItem = (
    <NavDropdown.Item as={Link} to={ROUTES.oms.customsNamePrompt} onClick={handleClose}>
      Customs Name Prompt
    </NavDropdown.Item>
  );
  const gigCartVerificationPromptItem = (
    <NavDropdown.Item as={Link} to={ROUTES.oms.gigCartVerificationPrompt} onClick={handleClose}>
      Gig Cart Verification Prompt
    </NavDropdown.Item>
  );
  const bundleRecipesItem = (
    <NavDropdown.Item as={Link} to={ROUTES.oms.bundleRecipes} onClick={handleClose}>
      Bundle Recipes
    </NavDropdown.Item>
  );
  const zplPrintItem = (
    <NavDropdown.Item as={Link} to={ROUTES.oms.zplPrint} onClick={handleClose}>
      Temporary ZPL Print
    </NavDropdown.Item>
  );

  // Nine dropdowns need more than a 1024 tablet, so the bar stays collapsed until xl.
  return (
    <BsNavbar bg="dark" variant="dark" expand="xl" expanded={expanded} onToggle={handleToggle} className="mb-3">
      <Container fluid>
        <BsNavbar.Brand as={Link} to={ROUTES.home}>
          SkinSeoul
        </BsNavbar.Brand>
        <BsNavbar.Toggle aria-controls="navbar-nav" />
        {/* The expanded bar needs ~1430px, so below that it wraps onto a second row rather than scrolling the page [F13.5]. */}
        <BsNavbar.Collapse id="navbar-nav" role="navigation" className="flex-wrap">
          <Nav className="me-auto">
            <NavDropdown title="Operation AI" id="ai-operations-dropdown">
              <NavDropdown.Item as={Link} to={ROUTES.ai.commandCenter} onClick={handleClose}>
                <LayoutDashboard size={16} className="me-2" />
                Agent Command Center
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.ai.agentRegistry} onClick={handleClose}>
                <Bot size={16} className="me-2" />
                Agent Registry
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.ai.skinMatchDemo} onClick={handleClose}>
                <ScanFace size={16} className="me-2" />
                Skin Match Demo
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.ai.skinAnalysisConfig} onClick={handleClose}>
                <FileQuestion size={16} className="me-2" />
                Skin Analysis Config
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.ai.recommendationLab} onClick={handleClose}>
                <FlaskConical size={16} className="me-2" />
                Recommendation Lab
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.mcpAccess.people} onClick={handleClose}>
                <KeyRound size={16} className="me-2" />
                MCP Access
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Header>Agent Telemetry</NavDropdown.Header>
              <NavDropdown.Item as={Link} to={ROUTES.agentTelemetry.overview} onClick={handleClose}>
                <Activity size={16} className="me-2" />
                Agent Telemetry
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.agentTelemetry.roleAssets} onClick={handleClose}>
                Role Assets
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.agentTelemetry.sharedAssetHealth} onClick={handleClose}>
                Shared Asset Health
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Header>Competitor Product Match</NavDropdown.Header>
              <NavDropdown.Item as={Link} to={ROUTES.competitor.v2} onClick={handleClose}>
                Competitor Matching V2
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.competitor.matchStatus} onClick={handleClose}>
                AI Match Competitor Status
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.competitor.matchSKU.default} onClick={handleClose}>
                Review AI Matched Product
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Header>Product Sourcing</NavDropdown.Header>
              <NavDropdown.Item as={Link} to={ROUTES.sourcing.matching} onClick={handleClose}>
                <GitMerge size={16} className="me-2" />
                GP-CP Matching
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.sourcing.matched} onClick={handleClose}>
                <CheckCircle size={16} className="me-2" />
                GP-CP Matched List
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.sourcing.ssReMatching} onClick={handleClose}>
                <GitMerge size={16} className="me-2" />
                SS CP Re-Matching
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.sourcing.scrapeBatch} onClick={handleClose}>
                <Gift size={16} className="me-2" />
                Scrape CP Batch
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.sourcing.coupangFailures} onClick={handleClose}>
                <CheckCircle size={16} className="me-2" />
                Coupang Failures
              </NavDropdown.Item>
            </NavDropdown>
            <NavDropdown title="Catalog Management" id="catalog-management-dropdown">
              <NavDropdown.Header>Product Management</NavDropdown.Header>
              <NavDropdown.Item as={Link} to={ROUTES.catalog.cmWorkflow} onClick={handleClose}>
                <Package size={16} className="me-2" />
                CM Workflow
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.catalog.ssEditor} onClick={handleClose}>
                <Edit size={16} className="me-2" />
                SS Editor
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.catalog.productGroups.list} onClick={handleClose}>
                <List size={16} className="me-2" />
                Product Groups
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.catalog.productTagMapping} onClick={handleClose}>
                <Tags size={16} className="me-2" />
                Product Tag Mapping
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.sourcing.categoryMapping} onClick={handleClose}>
                <List size={16} className="me-2" />
                GP-WP Category Mapping
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Header>Library</NavDropdown.Header>
              <NavDropdown.Item as={Link} to={ROUTES.category.ingredient.default} onClick={handleClose}>
                Ingredient Management
              </NavDropdown.Item>
            </NavDropdown>
            {launch !== 'on' && (
              <NavDropdown title="OMS Center" id="oms-center-dropdown">
                <NavDropdown.Item as={Link} to={ROUTES.oms.orders} onClick={handleClose}>
                  Order Management Dashboard
                </NavDropdown.Item>
                <NavDropdown.Item as={Link} to={ROUTES.oms.fulfillment} onClick={handleClose}>
                  Order Fulfillment Dashboard
                </NavDropdown.Item>
                {picManagementItem}
                {gigWorkersItem}
                <NavDropdown.Divider />
                <NavDropdown.Header>WMS</NavDropdown.Header>
                <NavDropdown.Item as={Link} to={ROUTES.oms.wms.viewOrders} onClick={handleClose}>
                  View Orders
                </NavDropdown.Item>
                <NavDropdown.Item as={Link} to={ROUTES.oms.wms.stock} onClick={handleClose}>
                  Stock Status
                </NavDropdown.Item>
                <NavDropdown.Item as={Link} to={ROUTES.oms.wms.readyToBeOutbonded} onClick={handleClose}>
                  Ready to be Outbonded
                </NavDropdown.Item>
                <NavDropdown.Divider />
                {shippingFeeItem}
                {customsNamePromptItem}
                {gigCartVerificationPromptItem}
                {bundleRecipesItem}
                <NavDropdown.Divider />
                {zplPrintItem}
              </NavDropdown>
            )}

            {/* V2 rebuild — its own domain, deliberately not nested under the V1 OMS menu */}
            <NavDropdown title="Fulfillment" id="fulfillment-dropdown">
              <NavDropdown.Item as={Link} to={ROUTES.fulfillment.dashboard} onClick={handleClose}>
                FC Dashboard
              </NavDropdown.Item>
              {/* "(OMS)" tells the order management list apart from View Orders, the desk's scan screen (SSD-7448). */}
              <NavDropdown.Item as={Link} to={ROUTES.fulfillment.orders} onClick={handleClose}>
                Orders (OMS)
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.fulfillment.viewOrders} onClick={handleClose}>
                View Orders
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.fulfillment.arrivalSort} onClick={handleClose}>
                Arrival Sort
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.fulfillment.readyToOutbound} onClick={handleClose}>
                Ready to be Outbounded
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.fulfillment.packingCheck} onClick={handleClose}>
                Packing
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.fulfillment.inventory} onClick={handleClose}>
                Inventory
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.fulfillment.unrecognizedScans} onClick={handleClose}>
                Unrecognized Tracking List
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.fulfillment.inboundRequests} onClick={handleClose}>
                Inbound Requests
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.fulfillment.closing} onClick={handleClose}>
                Daily Closing
              </NavDropdown.Item>
              {launch === 'on' && (
                <>
                  <NavDropdown.Divider />
                  <NavDropdown.Header>Tools</NavDropdown.Header>
                  {picManagementItem}
                  {gigWorkersItem}
                  {shippingFeeItem}
                  {customsNamePromptItem}
                  {gigCartVerificationPromptItem}
                  {bundleRecipesItem}
                  {zplPrintItem}
                </>
              )}
            </NavDropdown>

            <NavDropdown title="Site Management" id="site-management-dropdown">
              <li className="dropdown-submenu">
                <span className="dropdown-item dropdown-toggle">Banner</span>
                <ul className="dropdown-menu">
                  <li>
                    <Link className="dropdown-item" to={ROUTES.banner.upload} onClick={handleClose}>
                      Banner Upload
                    </Link>
                  </li>
                  <li>
                    <Link className="dropdown-item" to={ROUTES.banner.list} onClick={handleClose}>
                      Banner List
                    </Link>
                  </li>
                </ul>
              </li>
              <li className="dropdown-submenu">
                <span className="dropdown-item dropdown-toggle">Trending Videos</span>
                <ul className="dropdown-menu">
                  <li>
                    <Link className="dropdown-item" to={ROUTES.trendingVideo.upload} onClick={handleClose}>
                      Trending Video Upload
                    </Link>
                  </li>
                  <li>
                    <Link className="dropdown-item" to={ROUTES.trendingVideo.list} onClick={handleClose}>
                      Trending Video List
                    </Link>
                  </li>
                </ul>
              </li>
              <NavDropdown.Item as={Link} to={ROUTES.review.default} onClick={handleClose}>
                Review Management
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.survey.default} onClick={handleClose}>
                <FileQuestion size={16} className="me-2" />
                Survey
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.siteManagement.cache} onClick={handleClose}>
                Invalidate Site Cache
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.siteManagement.campaigns} onClick={handleClose}>
                Campaigns
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.siteManagement.shipping} onClick={handleClose}>
                Shipping Policies
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.siteManagement.ruleEngine} onClick={handleClose}>
                Rule Engine
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Header>System</NavDropdown.Header>
              <NavDropdown.Item as={Link} to={ROUTES.systemManagement.cronJobs} onClick={handleClose}>
                CronJobs
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.systemManagement.redirectConfiguration} onClick={handleClose}>
                Redirect Configuration
              </NavDropdown.Item>
              {secretsAccess.status === 'ready' ? (
                <>
                  <NavDropdown.Item as={Link} to={ROUTES.secrets.list} onClick={handleClose}>
                    <KeyRound size={16} className="me-2" />
                    Secrets
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to={ROUTES.secrets.requests} onClick={handleClose}>
                    Secret Requests
                  </NavDropdown.Item>
                </>
              ) : null}
            </NavDropdown>

            <NavDropdown title="Marketing" id="marketing-dropdown">
              <NavDropdown.Item as={Link} to={ROUTES.marketing.influencers} onClick={handleClose}>
                <Users size={16} className="me-2" />
                Influencer Explorer
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.marketing.brandManager} onClick={handleClose}>
                Brand Manager
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.marketing.campaignMatcher} onClick={handleClose}>
                Campaign Manager
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.marketing.templateManager} onClick={handleClose}>
                Template Manager
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.marketing.community} onClick={handleClose}>
                Community
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.marketing.affiliate} onClick={handleClose}>
                Affiliate
              </NavDropdown.Item>
            </NavDropdown>

            <NavDropdown title="Customer Management" id="customer-management-dropdown">
              <NavDropdown.Header>CS AI Agent</NavDropdown.Header>
              <NavDropdown.Item as={Link} to={ROUTES.customer.csPlayground} onClick={handleClose}>
                <MessageSquare size={16} className="me-2" />
                CS Playground
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.customer.csPromptEditor} onClick={handleClose}>
                <FileText size={16} className="me-2" />
                Prompt Editor
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.customer.knowledgeBase} onClick={handleClose}>
                <BookOpen size={16} className="me-2" />
                Knowledge Base
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Header>Loyalty Program</NavDropdown.Header>
              <NavDropdown.Item as={Link} to={ROUTES.customer.loyalty} onClick={handleClose}>
                <Users size={16} className="me-2" />
                Customer Loyalty
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={ROUTES.customer.tierManagement} onClick={handleClose}>
                <Award size={16} className="me-2" />
                Tier Management
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Header>Engagement</NavDropdown.Header>
              <NavDropdown.Item as={Link} to={ROUTES.customer.pushNotification} onClick={handleClose}>
                <Bell size={16} className="me-2" />
                Push Notification
              </NavDropdown.Item>
            </NavDropdown>

            {/* Brands we sell to, kept apart from the groups above, which all run our own storefront. */}
            <NavDropdown title="B2B" id="b2b-dropdown">
              <NavDropdown.Item as={Link} to={ROUTES.b2b.brandOnboarding} onClick={handleClose}>
                <Building2 size={16} className="me-2" />
                Brand Onboarding
              </NavDropdown.Item>
            </NavDropdown>

            {/* Short label on purpose: the bar wraps to a second row below 1600px, and the
                SkinSeoul prefix is redundant inside the SkinSeoul admin. */}
            <Nav.Link href={config.wpAdminUrl} target="_blank" rel="noopener noreferrer" onClick={handleClose}>
              WP Admin
            </Nav.Link>
          </Nav>
          {user && (
            <Nav className="ms-auto">
              <Nav.Item className="d-flex align-items-center">
                {/* Mentions follow the operator, not the screen, so the hub travels with their corner [G-7]. */}
                <span className="me-3">
                  <CommentsHub variant="outline-light" showLabel={false} />
                </span>
                <img
                  src={user.picture}
                  alt="Profile"
                  className="rounded-circle me-2"
                  style={{ width: '30px', height: '30px' }}
                />
                <span className="text-light me-3">{user.name}</span>
                <Button variant="outline-light" size="sm" onClick={logout}>
                  Logout
                </Button>
              </Nav.Item>
            </Nav>
          )}
        </BsNavbar.Collapse>
      </Container>
    </BsNavbar>
  );
};

export default Navbar;
